import React, { useState, useRef, useEffect } from 'react';
import { Send, AtSign, Loader2 } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * MentionInput - Comment input with @mention autocomplete
 * Usage: <MentionInput value={val} onChange={setVal} onSubmit={handleSubmit} placeholder="..." />
 */
export function MentionInput({ value, onChange, onSubmit, placeholder, submitting, className }) {
  const [showMentions, setShowMentions] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [members, setMembers] = useState([]);
  const [membersLoaded, setMembersLoaded] = useState(false);
  const [cursorPos, setCursorPos] = useState(0);
  const inputRef = useRef(null);

  // Load team members once
  useEffect(() => {
    if (showMentions && !membersLoaded) {
      const token = localStorage.getItem('token');
      axios.get(`${API}/team-members`, { headers: { Authorization: `Bearer ${token}` } })
        .then(res => { setMembers(res.data.members || []); setMembersLoaded(true); })
        .catch(() => {});
    }
  }, [showMentions, membersLoaded]);

  const handleChange = (e) => {
    const val = e.target.value;
    const pos = e.target.selectionStart;
    onChange(val);
    setCursorPos(pos);

    // Check if we're typing a @mention
    const textBefore = val.substring(0, pos);
    const atMatch = textBefore.match(/@(\w*)$/);
    if (atMatch) {
      setMentionQuery(atMatch[1].toLowerCase());
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !showMentions) {
      e.preventDefault();
      onSubmit?.();
    }
    if (e.key === 'Escape') {
      setShowMentions(false);
    }
  };

  const insertMention = (name) => {
    const textBefore = value.substring(0, cursorPos);
    const textAfter = value.substring(cursorPos);
    const atIdx = textBefore.lastIndexOf('@');
    const newText = textBefore.substring(0, atIdx) + `@${name} ` + textAfter;
    onChange(newText);
    setShowMentions(false);
    inputRef.current?.focus();
  };

  const filteredMembers = [
    { id: 'all', name: 'all', label: 'Everyone', avatar_url: '' },
    ...members
  ].filter(m => !mentionQuery || m.name.toLowerCase().includes(mentionQuery));

  return (
    <div className="relative flex gap-2 w-full">
      <div className="relative flex-1">
        <input
          ref={inputRef}
          value={value}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || "Write a comment... (type @ to mention)"}
          className={`w-full h-9 text-sm bg-[hsl(var(--surface))] border border-[hsl(var(--border))] rounded-lg px-3 focus:outline-none focus:ring-1 focus:ring-[hsl(var(--primary))] ${className || ''}`}
        />

        {/* @Mention Dropdown */}
        {showMentions && (
          <div className="absolute bottom-full left-0 mb-1 w-64 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl shadow-2xl overflow-hidden z-[9999]">
            <div className="p-2 border-b border-[hsl(var(--border))] flex items-center gap-1.5 text-[10px] text-[hsl(var(--text-muted))] uppercase tracking-wider">
              <AtSign size={10} /> Mention someone
            </div>
            <div className="max-h-40 overflow-y-auto">
              {filteredMembers.length === 0 ? (
                <div className="p-3 text-xs text-[hsl(var(--text-muted))] text-center">No users found</div>
              ) : filteredMembers.slice(0, 8).map(m => (
                <button
                  key={m.id}
                  onClick={() => insertMention(m.name)}
                  data-testid={`mention-option-${m.id}`}
                  className="w-full flex items-center gap-2 p-2 hover:bg-[hsl(var(--surface))] transition-colors text-left"
                >
                  {m.id === 'all' ? (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-[9px] font-bold">@</div>
                  ) : m.avatar_url ? (
                    <img src={m.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
                  ) : (
                    <div className="w-6 h-6 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-[9px] font-bold">{m.name?.charAt(0)}</div>
                  )}
                  <div>
                    <p className="text-xs font-medium">{m.id === 'all' ? '@all — Everyone' : m.name}</p>
                    {m.department && <p className="text-[10px] text-[hsl(var(--text-muted))]">{m.department}</p>}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
      <button
        onClick={onSubmit}
        disabled={submitting || !value?.trim()}
        className="h-9 px-3 bg-gradient-to-r from-pink-500 to-rose-600 text-white rounded-lg disabled:opacity-40 flex items-center"
      >
        {submitting ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
      </button>
    </div>
  );
}

export default MentionInput;
